import React from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import { RootState } from 'store';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import FlightsDetails from 'components/FlightsDetails';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { ConfirmMessage } from 'models/confirmMessage';
import { FlightValidationResponse } from 'models/flightValidation';
import SolicitationSuccess from 'screens/components/SolicitationSuccess';
import { amplitudelogEvent, ProceedToCheckoutEvent } from 'services/amplitudeService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, htmlT } from 'utils/generic';

import ConfirmApproveModal from '../Dashboard/components/ConfirmApproveModal';
import CancellationPolicies from './components/CancellationPolicies';
import Checkout from './components/Checkout';
import Price from './components/Price';
import { Container, Body, SectionContainer } from './styles';

export interface Props {
  show: boolean;
  creating: boolean;
  loading: boolean;
  details?: FlightValidationResponse;
  onConfirm: (
    flight: FlightValidationResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
  ) => void;
  onClose: () => void;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  showSuccess: boolean;
  traveler?: TravelArrangerUsersAllowed;
  showConfirmFlow: boolean;
  confirmFlowMessage: ConfirmMessage;
  onConfirmFlow: () => void;
  onConfirmFlowClose: () => void;
  zIndex?: number;
}

const FlightDetails = ({
  show,
  creating,
  loading,
  details,
  onClose,
  onConfirm,
  showChurning,
  setShowChurning,
  showSuccess,
  traveler,
  onConfirmFlow,
  showConfirmFlow,
  confirmFlowMessage,
  onConfirmFlowClose,
  zIndex,
}: Props) => {
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const search = useSelector((state: RootState) => state.flights);
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);

  const handleConfirm = (
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
  ) => {
    if (!details) return;

    amplitudelogEvent(amplitudeEvents.PROCEED_TO_CHECKOUT, amplitudeProceedToCheckoutEvent, userAccount);

    onConfirm(details, justifyForBreakingRules, reason, costCenterId, breakRulesMessageId);
  };

  const amplitudeProceedToCheckoutEvent: ProceedToCheckoutEvent = {
    SOURCE: 'BOOKING',
    TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
    TRAVEL_FROM: search?.departurePoint?.code ?? '',
    TRAVEL_TO: search?.arrivalPoint?.code ?? '',
    TRAVEL_DESTINATION_TYPE: '',
    TRAVEL_TRIP_TYPE: '',
    TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), search?.departureDate).toString(),
    TRAVEL_DATE_FROM: search?.departureDate?.toString() ?? '',
    TRAVEL_DATE_TO: search?.returnDate?.toString() ?? '',
    TRAVEL_TRIP_LENGTH: (daysCount(search?.departureDate, search?.returnDate) + 1).toString(),
    TRAVEL_PASSENGERS: '1',
    TRAVEL_ADULTS: '1',
    TRAVEL_CHILDREN: '0',
    TRAVEL_INFANTS: '0',
    TRAVEL_CLASS_OF_SERVICE: '',
    TRAVEL_ROOMS: '1',
    TRAVEL_WITH_BAGGAGE: '',
    TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
    SEARCHID: search?.search?.searchId ?? '',
    TRAVEL_PRODUCT_CODE: details?.tripOption.sectors.first().flights.first().airlineCode ?? '',
    TRAVEL_PRODUCT_NAME: details?.tripOption.sectors.first().flights.first().airlineName ?? '',
    TRAVEL_SUB_PRODUCT: details?.tripOption.sectors.first().flights.first().fareFamilies ?? '',
    TRAVEL_PRODUCT_CATEGORY: details?.tripOption.sectors.first().flights.first().cabin ?? '',
    TRAVEL_PRODUCT_PRICE: '',
    TRAVEL_PRODUCT_PROVIDER: details?.tripOption.provider ?? '',
    TRAVEL_FARE_TYPE: '',
  };

  const renderContent = () => {
    if (details) {
      return (
        <>
          <Body>
            <FlightsDetails breakRulesMessage={details.breakRulesMessage} sectors={details.tripOption.sectors} />
            <SectionContainer>
              <CancellationPolicies policies={details.airFareConditions} />
              <Price price={details.tripOption.price} />
            </SectionContainer>
            <br />
            <br />
            <Checkout
              loading={creating}
              isBestOption={false}
              onConfirm={handleConfirm}
              onClose={onClose}
              price={details.tripOption.price}
              isPreApproved={Boolean(details.isPreApproved)}
              showChurning={showChurning}
              setShowChurning={setShowChurning}
              details={details}
            />
          </Body>

          <SolicitationSuccess
            show={showSuccess}
            isHotel={false}
            isFlight={true}
            testId="flight-solicitation-success"
          />
        </>
      );
    }
  };

  return (
    <>
      <ApplicationOverlayContent show={show} width={600} onClose={onClose} testId="flight-details" zIndex={zIndex}>
        <Container>
          <Mobile>
            <AppBar goBack={onClose}>{t('flightDetails.title')}</AppBar>
          </Mobile>

          <Desktop>
            <ModalHeader
              onClose={onClose}
              title={t('flightDetails.title')}
              subtitle={
                traveler ? (
                  <>{htmlT(t, 'flightsSearch.toTraveler', { user: `${traveler.name} ${traveler.lastName}` })}</>
                ) : undefined
              }
            />
          </Desktop>

          {loading ? <Loader fullScreen={isMobile} /> : renderContent()}
        </Container>
      </ApplicationOverlayContent>

      <ConfirmApproveModal
        onConfirmApprove={onConfirmFlow}
        onConfirmClose={onConfirmFlowClose}
        showConfirm={showConfirmFlow}
        confirmMessage={confirmFlowMessage}
        showCancelButton={false}
      ></ConfirmApproveModal>
    </>
  );
};

export default FlightDetails;
